import React, { useEffect, useState } from 'react';
import { Col, Row, Spinner, Input, InputGroupText, InputGroup } from 'reactstrap';
import { useSelector } from 'react-redux';
import { FaDownload } from 'react-icons/fa';
import DateRangePicker from '../../../sharedComponents/DateRangePicker/DateRangePicker';
import Title from '../../../sharedComponents/Title/Title';
import useDashboard from '../useDashboard';
import { BsSearch } from 'react-icons/bs';
import moment from 'moment';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme } from '../../../config/constants';
import { isMobileOnly } from 'react-device-detect';
import { Table, Column, AutoSizer } from 'react-virtualized';

const BEPDistributorListCard = () => {
    const isLoading = useSelector(({dashboard}) => dashboard.bepDistributorListIsLoading);
    const bepDistributorListData = useSelector(({dashboard}) => dashboard.bepDistributorListData);
    const { getBEPDistributorListData, onClickDownloadCsv } = useDashboard.useDates();
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState(null);
    const [inputText, setInputText] = useState('');
    const [dateRange, setDateRange] = React.useState({
        start: moment().subtract(13, "month").startOf("day"),
        end: moment().endOf("day"),
    });

    const handleFilterChange = React.useCallback((next = {}) => {
        if (Object.prototype.hasOwnProperty.call(next, "text")) setInputText(next.text ?? ""); 
        if (Object.prototype.hasOwnProperty.call(next, "start") ||
            Object.prototype.hasOwnProperty.call(next, "end")) {
        const toMoment = (momInput) => {
            if (moment.isMoment(momInput)) return momInput;
            if (momInput instanceof Date) return moment(momInput);
            if (typeof momInput === "string") {
                const mom = moment(momInput, ["MM/DD/YYYY", "YYYY-MM-DD", "M-YYYY", "MM-YYYY"], true);
                return mom.isValid() ? mom : moment.invalid();
            }
            return moment.invalid();
        };
        setDateRange((prev) => ({
            start: next.start ? toMoment(next.start).startOf("day") : prev.start,
            end: next.end ? toMoment(next.end).endOf("day") : prev.end,
        }));
        }
    }, []);

    const parseRowDate = React.useCallback((d) => {
        const m = moment(String(d ?? ""), ["M-YYYY", "MM-YYYY", "MM/DD/YYYY", "YYYY-MM-DD"], true);
        return m.isValid() ? m.startOf("day") : moment.invalid();
    }, []);

    const titledList = React.useMemo(() => {
        return (bepDistributorListData ?? []).map((row) => {
            const dateSplit = row.date.split('-');
            const formattedTitle = `BEP_Distributor_List_${dateSplit[0].length <= 1 ? '0' + dateSplit[0] : dateSplit[0]}_${dateSplit[1]}`;
            return {
                id: row?.id,
                date: row?.date,
                filename: formattedTitle,
                dateMoment: parseRowDate(row?.date)
            }
        });
    }, [bepDistributorListData, parseRowDate])

    const initializePage = () => {
        getBEPDistributorListData();
    }

    useEffect(initializePage
        //  eslint-disable-next-line  
      , []);

    const rows = React.useMemo(() => {
        const inputTextLowercase = inputText.trim().toLowerCase();

        return titledList.filter((row) => {
            const textMatch = !inputTextLowercase || row.filename?.toLowerCase().includes(inputTextLowercase) || String(row.date ?? "").toLowerCase().includes(inputTextLowercase) || String(row.id ?? "").includes(inputTextLowercase);
            if (!row.dateMoment.isValid()) return false;
            const inRange = (!dateRange.start || row.dateMoment.isSameOrAfter(dateRange.start, "day")) && (!dateRange.end || row.dateMoment.isSameOrBefore(dateRange.end, "day"));

            return textMatch && inRange;
        })
        .sort((a,b) => b.dateMoment.valueOf() - a.dateMoment.valueOf());
    }, [titledList, inputText, dateRange.start, dateRange.end]);

    if (isLoading || bepDistributorListData.length <= 0) {
        return (
            <Col style={{ height: '100%' }}>
                <Title title='BEP Distributor List' />
                <Row className='mb-2'>
                    <Col className='col-4 pe-1'>
                        <InputGroup size='sm'>
                            <InputGroupText>
                                <BsSearch/>
                            </InputGroupText>
                            <Input className='text-input' id="csvSearchInput" placeholder='Search...' onChange={(searchText) => handleFilterChange({ text: searchText.target.value })}/> 
                        </InputGroup>
                    </Col>
                    <Col className='w-fc'>
                        <DateRangePicker 
                            id='csvDateSearch' 
                            defaultStartDate={moment().subtract(13, 'month').format('MM/DD/YYYY')} 
                            defaultEndDate={moment().format('MM/DD/YYYY')} 
                            minDate={moment().subtract(13, 'month').format('MM/DD/YYYY')} 
                            maxDate={moment().format('MM/DD/YYYY')} 
                            callback={(payloadOrStart, maybeEnd) => {
                                const startDate = payloadOrStart?.startDate ?? payloadOrStart ?? dateRange.start;
                                const endDate = payloadOrStart?.endDate ?? maybeEnd ?? dateRange.end;
                                handleFilterChange({ start: startDate, end: endDate });
                            }}
                        />
                    </Col>
                </Row>
                <Row style={{height: '75%', overflowY: 'auto'}}>
                    <div>
                        {isLoading &&
                        <div className="mx-auto my-auto" style={{height:"2.5%", width:"2.5%"}}>
                            <Spinner color='beplBlue' animation="border" />
                        </div>
                        }
                        {!isLoading &&
                        <EmptyDataMessage />
                        }
                    </div>
                </Row>
            </Col>
        );
    }

    return (
        <Col style={{ height: '100%', overflow: 'hidden' }}>
            <Title title='BEP Distributor List' />
            <Row className='mb-2'>
                <Col className='col-4 pe-1'>
                    <InputGroup size='sm'>
                        <InputGroupText>
                            <BsSearch/>
                        </InputGroupText>
                        <Input className='text-input' id="csvSearchInput" placeholder='Search...' onChange={(searchText) => handleFilterChange({ text: searchText.target.value })}/> 
                    </InputGroup>
                </Col>
                <Col className='w-fc'>
                    <DateRangePicker 
                        id='csvDateSearch' 
                        defaultStartDate={moment().subtract(13, 'month').format('MM/DD/YYYY')} 
                        defaultEndDate={moment().format('MM/DD/YYYY')} 
                        minDate={moment().subtract(13, 'month').format('MM/DD/YYYY')} 
                        maxDate={moment().format('MM/DD/YYYY')} 
                        callback={(payloadOrStart, maybeEnd) => {
                            const startDate = payloadOrStart?.startDate ?? payloadOrStart ?? dateRange.start;
                            const endDate = payloadOrStart?.endDate ?? maybeEnd ?? dateRange.end;
                            handleFilterChange({ start: startDate, end: endDate });
                        }}
                        ranges={{
                            'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                            'Last 2 Months': [moment().subtract(2, 'month'), moment()],
                            'Last Year': [moment().subtract(1, 'year'), moment()],
                            'Last 13 Months': [moment().subtract(13, 'month'), moment()],
                            'Last Two Years': [moment().subtract(2, 'year'), moment()]
                            }
                        }
                    />
                </Col>
            </Row>
            <Row style={{ height: '75%', overflowY: 'auto' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AutoSizer>
                        {({ height, width }) => (
                            <Table
                                width={width}
                                height={height}
                                headerHeight={40}
                                rowHeight={40}
                                rowCount={(rows.length)}
                                rowGetter={({ index }) => rows[index]}
                                sort={({ sortBy: sb, sortDirection: sd }) => {
                                    setSortBy(sb);
                                    setSortDirection(sd);
                                }}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                className="table topCategoriesTable border-grayLight"
                            >
                                <Column
                                    label="FILE NAME"
                                    dataKey="filename"
                                    width={width * 0.6}
                                    cellRenderer={({ rowData }) => {
                                        const name = rowData.filename;
                                        return <span>{name}</span>;
                                    }}
                                />
                                <Column
                                    label="DATE CREATED"
                                    dataKey="date"
                                    width={width * 0.3}
                                    cellRenderer={({ cellData }) => {
                                        const dateSplit = cellData.split('-');
                                        return <span>{(dateSplit[0].length <= 1 ? '0' + dateSplit[0] : dateSplit[0]) + '-' + dateSplit[1]}</span>;
                                    }}
                                />
                                <Column
                                    label=""
                                    dataKey="id" 
                                    width={width * 0.1}
                                    cellRenderer={({ rowData }) => (
                                        <span
                                            className="clickable"
                                            style={{ color: Theme().colors.accent }}
                                            onClick={() => onClickDownloadCsv(rowData.id)}
                                        >
                                            <FaDownload /> {isMobileOnly ? '' : 'Download'}
                                        </span>
                                    )}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </div>
            </Row>
        </Col>
    );
};

export default BEPDistributorListCard;